/* RESPONSIBLE TEAM: team-ai-agent-2 */

import type AiContentSegment from 'embercom/models/ai-content-segment';
import { MAX_NB_CHARS_FOR_AUDIENCES_NAME } from 'embercom/services/ai-content-segments-service';
import type PlaygroundApi from 'embercom/services/playground-api';
import type IntlService from 'ember-intl/services/intl';
import type Brand from 'embercom/models/brand';
import type { PlaygroundSettings } from 'embercom/lib/fin-playground/settings';

export function firstSegmentNameFromIds(
  segments: Array<AiContentSegment>,
  selectedSegmentIds: Array<string>,
  shouldTruncate = true,
) {
  let firstSegmentName = segments.find(
    (segment) => Number(selectedSegmentIds[0]) === Number(segment.id),
  )?.nameWithFallback;

  if (
    shouldTruncate &&
    firstSegmentName &&
    firstSegmentName?.length > MAX_NB_CHARS_FOR_AUDIENCES_NAME
  ) {
    firstSegmentName = `${firstSegmentName.substring(0, MAX_NB_CHARS_FOR_AUDIENCES_NAME)}...`;
  }

  return firstSegmentName;
}

export function getTestingAsLabel({
  playgroundApi,
  audienceSegments,
  intl,
  shouldTruncate = true,
}: {
  playgroundApi: PlaygroundApi;
  audienceSegments: Array<AiContentSegment>;
  intl: IntlService;
  shouldTruncate?: boolean;
}): string {
  let usingAudienceTargeting = playgroundApi.usingAudienceTargeting;
  let selectedContentSegmentIds = playgroundApi.selectedContentSegmentIds;
  let impersonatedUser = playgroundApi.impersonatedUser;

  if (usingAudienceTargeting) {
    if (selectedContentSegmentIds?.length) {
      let firstSegmentName = firstSegmentNameFromIds(
        audienceSegments,
        selectedContentSegmentIds,
        shouldTruncate,
      );
      if (selectedContentSegmentIds.length === 1) {
        return (
          firstSegmentName ??
          intl.t('knowledge-hub.content-editor.shared.audience-section.dropdown.everyone')
        );
      }
      return intl.t('ai-agent.playground.settings.audience-filter.additional-segments', {
        segmentName: firstSegmentName,
        count: selectedContentSegmentIds.length - 1,
      });
    }
    return intl.t('knowledge-hub.content-editor.shared.audience-section.dropdown.everyone');
  } else {
    return impersonatedUser?.displayAs ?? intl.t('operator.custom-bot.editor.preview.preview-user');
  }
}

export function getBrandDropdownLabel({
  brandId,
  brands,
  appName,
  intl,
  disabled = false,
  defaultBrandId = 'all',
}: {
  brandId: string | null | undefined;
  brands: Array<Brand>;
  appName: string;
  intl: IntlService;
  disabled?: boolean;
  defaultBrandId?: string;
}): string {
  if (disabled) {
    return intl.t('ai-agent.playground.settings.brand-filter.all-brands');
  }

  if (brandId && brandId !== defaultBrandId) {
    let brand = brands.find((b) => b.id === brandId);
    if (brand) {
      return brand.name;
    }
  }
  return appName;
}

export function shouldShowBrand({
  withBrandDropdown,
  usingAudienceTargeting,
  isStandaloneApp,
}: {
  withBrandDropdown: boolean;
  usingAudienceTargeting: boolean;
  isStandaloneApp: boolean;
}): boolean {
  if (!withBrandDropdown) {
    return false;
  }

  if (usingAudienceTargeting) {
    return false;
  }

  if (isStandaloneApp) {
    return false;
  }

  return true;
}

export function isBrandDisabled(playgroundSettings: PlaygroundSettings): boolean {
  return playgroundSettings.usingAudienceTargeting || !playgroundSettings.userId;
}
